/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useRef, useState } from 'react'
import {
	onClipboardClickHandler,
	type ClipboardHandlerArgs,
} from '../../utils/clipboard'
import classNames from 'classnames'
import { Button } from '../../components/button'
import s from './style.module.css'

export interface CopyButtonProps {
	size?: 'small' | 'medium'
	isIconOnly?: boolean
	isFullWidth?: boolean
	text: string
	textToCopy?: string
	getTextFn?: () => string
	targetToCopy?: string | Element
	onSuccess?: (args: ClipboardHandlerArgs) => void
	onError?: (args: ClipboardHandlerArgs) => void
	className?: string
	defaultIcon?: string
}

const DEFAULT_ICON = 'clipboard-copy'
const SUCCESS_ICON = 'clipboard-checked'
const ERROR_ICON = 'clipboard-x'
const DEFAULT_STATUS = 'idle'

const CopyButton = ({
	size = 'medium',
	isIconOnly = false,
	isFullWidth = false,
	text,
	textToCopy,
	getTextFn,
	targetToCopy,
	onSuccess,
	onError,
	className,
	defaultIcon,
	...rest
}: CopyButtonProps) => {
	const [status, setStatus] = useState(DEFAULT_STATUS)
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
	const icon =
		status === 'idle'
			? defaultIcon || DEFAULT_ICON
			: status === 'success'
				? SUCCESS_ICON
				: ERROR_ICON

	const resetStatusDelayed = () => {
		if (timer.current) {
			clearTimeout(timer.current)
		}
		timer.current = setTimeout(() => {
			setStatus(DEFAULT_STATUS)
		}, 1500)
	}

	const _onSuccess = (args: ClipboardHandlerArgs) => {
		setStatus('success')
		resetStatusDelayed()

		if (onSuccess) {
			onSuccess(args)
		}
	}

	const _onError = (args: ClipboardHandlerArgs) => {
		setStatus('error')
		resetStatusDelayed()

		if (onError) {
			onError(args)
		}
	}

	return (
		<Button
			className={classNames(s['copy-button'], s[status], className)}
			size={size}
			isFullWidth={isFullWidth}
			text={text}
			icon={icon}
			isIconOnly={isIconOnly}
			color="secondary"
			iconPosition="trailing"
			{...onClipboardClickHandler({
				text: textToCopy,
				getTextFn: getTextFn,
				target: targetToCopy,
				onSuccess: _onSuccess,
				onError: _onError,
			})}
			{...rest}
		/>
	)
}

CopyButton.displayName = 'CopyButton'

export { CopyButton }
